/** Keep in sync with backend/lib/reportPermissions.js */
export const REPORT_IDS = [
  'today',
  'sales-summary',
  'sales-trends',
  'payment-methods',
  'item-sales',
  'category-sales',
  'modifier-sales',
  'discounts-comps',
  'taxes',
  'service-charges',
  'voids',
  'cash-drawers',
  'gift-cards',
  'team-sales',
  'tips',
  'labor',
  'labor-vs-sales',
  'attendance',
  'schedule-vs-payroll',
  'timecard-requests'
];

export const VIEW_REPORT_MONEY = 'view_report_money';
export const VIEW_REPORT_NUMBERS = 'view_report_numbers';
export const VIEW_LABOR_DOLLARS = 'view_labor_dollars';
export const EXPORT_REPORTS_CSV = 'export_reports_csv';

/** Some reports reuse another report’s permission (e.g. Today → Sales Summary). */
const REPORT_PERMISSION_ALIASES = {
  today: 'sales-summary'
};

export function permissionForReportId(reportId) {
  const aliased = REPORT_PERMISSION_ALIASES[reportId] || reportId;
  return `view_report_${String(aliased || '').replace(/-/g, '_')}`;
}

/** Fallback nav while /api/reports/catalog is in flight. Keep in sync with backend REPORT_DEFS. */
export const REPORT_NAV_DEFS = [
  { id: 'today', name: 'Today', category: 'Overview' },
  { id: 'sales-summary', name: 'Sales Summary', category: 'Sales and transactions' },
  { id: 'sales-trends', name: 'Sales Trends', category: 'Sales and transactions' },
  { id: 'payment-methods', name: 'Payment Methods', category: 'Sales and transactions' },
  { id: 'item-sales', name: 'Item Sales', category: 'Sales and transactions' },
  { id: 'category-sales', name: 'Category Sales', category: 'Sales and transactions' },
  { id: 'modifier-sales', name: 'Modifier Sales', category: 'Sales and transactions' },
  { id: 'discounts-comps', name: 'Discounts & Comps', category: 'Sales and transactions' },
  { id: 'taxes', name: 'Taxes', category: 'Sales and transactions' },
  { id: 'service-charges', name: 'Fees & Service Charges', category: 'Sales and transactions' },
  { id: 'voids', name: 'Voids', category: 'Sales and transactions' },
  { id: 'cash-drawers', name: 'Cash Drawers', category: 'Sales and transactions' },
  { id: 'gift-cards', name: 'Gift Cards', category: 'Sales and transactions' },
  { id: 'team-sales', name: 'Team Sales', category: 'Team' },
  { id: 'tips', name: 'Tips', category: 'Team' },
  { id: 'labor', name: 'Labor', category: 'Team' },
  { id: 'labor-vs-sales', name: 'Labor vs. Sales', category: 'Team' },
  { id: 'attendance', name: 'Attendance', category: 'Team' },
  { id: 'schedule-vs-payroll', name: 'Schedule vs. Payroll', category: 'Team' },
  { id: 'timecard-requests', name: 'Timecard Requests', category: 'Team' }
].map((def) => ({
  ...def,
  permission: permissionForReportId(def.id)
}));

export const REPORT_PERMISSION_NAMES = REPORT_IDS.map(permissionForReportId);

export const REPORT_FIELD_PERMISSION_NAMES = [
  VIEW_REPORT_MONEY,
  VIEW_REPORT_NUMBERS,
  VIEW_LABOR_DOLLARS,
  EXPORT_REPORTS_CSV
];

/** Any of these grants access to the Reports page / nav. */
export const REPORTS_PAGE_PERMISSIONS = REPORT_PERMISSION_NAMES;

/** Order in Users / permission preview (reports first, then field gates). */
export const REPORT_PERMISSION_ORDER = [
  ...REPORT_PERMISSION_NAMES,
  ...REPORT_FIELD_PERMISSION_NAMES
];

/**
 * Labor $ / labor-% fields. Gated by view_labor_dollars (not only view_report_money)
 * so wage-equivalent totals are not leaked via cost ÷ hours.
 */
export const LABOR_DOLLAR_KEYS = new Set([
  'laborCost',
  'openLaborCost',
  'overtimeLaborCost',
  'doubletimeLaborCost',
  'laborCostWithOt',
  'workweekLaborCost',
  'overtimeCost',
  'doubletimeCost',
  'laborCostPct',
  'laborCostWithOtPct'
]);

export const MONEY_SUMMARY_KEYS = new Set([
  'grossSales',
  'returns',
  'discounts',
  'comps',
  'compsAndDiscounts',
  'netSales',
  'tax',
  'tips',
  'totalCollected',
  'giftCardSales',
  'avgNetSales',
  'tipPool',
  'estimatedTips',
  'declaredTips',
  'attributedTips',
  'tipPerHour',
  'laborCost',
  'openLaborCost',
  'overtimeLaborCost',
  'doubletimeLaborCost',
  'laborCostWithOt',
  'salesPerLaborHour',
  'workweekLaborCost',
  'activationsAmount',
  'redemptionsAmount',
  'netAmount'
]);

export const NUMBER_SUMMARY_KEYS = new Set([
  'orderCount',
  'uniqueCustomers',
  'tipEligibleHours',
  'tipEligibleShifts',
  'laborHours',
  'openLaborHours',
  'openTimecards',
  'overtimeHours',
  'doubletimeHours',
  'laborCostPct',
  'laborCostWithOtPct',
  'cardsSold',
  'activationsEvents',
  'redemptionsEvents',
  'lateClockIns',
  'lateClockInMinutes',
  'lateClockInHours',
  'hoursRequested',
  'hoursRequestedNet',
  'employeesWithRequests',
  'earlyClockIns',
  'earlyClockInMinutes',
  'lateClockOuts',
  'lateClockOutMinutes',
  'earlyClockOuts',
  'earlyClockOutMinutes',
  'missedClockIns',
  'unscheduledPunches',
  'matchedShifts',
  'employeesWithLates',
  'gapMinutes',
  'gapCount',
  'avgCoveragePct',
  'netOverMinutes',
  'wrongLocationCount',
  'scheduledHours',
  'payrollHours',
  'diffHours',
  'weekCount',
  'overWeeks',
  'underWeeks',
  'matchWeeks',
  'employeesCompared',
  'employeesOver',
  'employeesUnder',
  'unmatchedPayrollPeople',
  'duplicateWeeksSkipped',
  'requestCount',
  'requestedShiftCount',
  'clockInCount',
  'requestRate',
  'hoursChanged',
  'hoursChangedAbs',
  'employeesWithRequests',
  'unmatchedCount',
  'parseErrorCount',
  'hoursKnownCount'
]);

export const ITEM_FIELD_TYPES = {
  qtySold: 'number',
  qtyReturned: 'number',
  netQty: 'number',
  grossSales: 'currency',
  netSales: 'currency',
  orders: 'number',
  hours: 'number',
  scheduledHours: 'number',
  payrollHours: 'number',
  regularHours: 'number',
  overtimeHours: 'number',
  overtimeCost: 'currency',
  doubletimeHours: 'number',
  doubletimeCost: 'currency',
  tips: 'currency',
  diffHours: 'number',
  laborCost: 'currency',
  laborCostWithOt: 'currency',
  declaredTips: 'currency',
  shifts: 'number',
  avgWage: 'currency',
  tippedWage: 'currency',
  requestCount: 'number',
  requestedShiftCount: 'number',
  clockInCount: 'number',
  clockInPunchCount: 'number',
  requestRate: 'percent',
  hoursChanged: 'number',
  hoursChangedAbs: 'number',
  lateInMinutes: 'number',
  lateInHours: 'number',
  hoursRequested: 'number',
  hoursRequestedNet: 'number'
};

export function shouldRedactType(type, { money, numbers }) {
  if (type === 'currency') return !money;
  if (type === 'number' || type === 'percent') return !numbers;
  return false;
}

/** Column.requires / requiresAny gate (e.g. view_timecards for hours). Both must pass when present (AND). */
export function columnVisibleForUser(col, { money, numbers, canViewRequires }) {
  if (!col) return false;
  if (col.type === 'currency' && !money) return false;
  if ((col.type === 'number' || col.type === 'percent') && !numbers) return false;
  if (col.requires) {
    if (typeof canViewRequires !== 'function') return false;
    const names = Array.isArray(col.requires) ? col.requires : [col.requires];
    if (!names.some((name) => canViewRequires(name))) return false;
  }
  if (col.requiresAny) {
    if (typeof canViewRequires !== 'function') return false;
    const names = Array.isArray(col.requiresAny) ? col.requiresAny : [col.requiresAny];
    if (!names.some((name) => canViewRequires(name))) return false;
  }
  return true;
}
